import type { ResourcesConfig } from "aws-amplify";
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";
import { getActiveRegion } from "./active-region";
import type { Environment } from "./aws-configuration";

type EnvironmentExports = {
	primary: ResourcesConfig;
	failover: ResourcesConfig;
};

const AwsExportsConfig: Record<Environment, EnvironmentExports> = {
	// AWS Account: JcaOutrunFlightpath-Dev (963716652507)
	Dev: {
		primary: {
			Auth: {
				Cognito: {
					identityPoolId:
						"us-east-1:b125b7c5-eb2b-45c4-9245-0013be2d80d3",
					userPoolId: "us-east-1_K8rQqu9i3",
					userPoolClientId: "541oi62srqqh84p3sgbluahrj9",
					userPoolEndpoint: "https://cognito-idp.us-east-1.amazonaws.com",
				},
			},
			Storage: {
				S3: {
					bucket: "outrun-system-software-beta",
					region: "us-east-1",
				},
			},
		},
		failover: {
			Auth: {
				Cognito: {
					// Cognito user pool replication keeps userPoolId identical across
					// regions - only the identity pool and endpoint region change.
					identityPoolId:
						"us-west-2:1a219521-46b7-41f8-8461-248256d0a79e",
					userPoolId: "us-east-1_K8rQqu9i3",
					userPoolClientId: "541oi62srqqh84p3sgbluahrj9",
					userPoolEndpoint: "https://cognito-idp.us-west-2.amazonaws.com",
				},
			},
			Storage: {
				S3: {
					bucket: "outrun-system-software-beta-us-west-2",
					region: "us-west-2",
				},
			},
		},
	},
	// AWS Account: JcaOutrunFlightpath (414068118687)
	Prod: {
		primary: {
			Auth: {
				Cognito: {
					identityPoolId:
						"us-east-1:250b3b10-812c-42b2-bbe7-5c175ed5c36d",
					userPoolId: "us-east-1_j2zwt6der",
					userPoolClientId: "7t697i8h5pdg2jgqd0s1348vqk",
					userPoolEndpoint: "https://cognito-idp.us-east-1.amazonaws.com",
				},
			},
			Storage: {
				S3: {
					bucket: "outrun-system-software",
					region: "us-east-1",
				},
			},
		},
		failover: {
			Auth: {
				Cognito: {
					identityPoolId:
						"us-west-2:e1314f16-a983-4944-8d95-25272a859469",
					userPoolId: "us-east-1_j2zwt6der",
					userPoolClientId: "7t697i8h5pdg2jgqd0s1348vqk",
					userPoolEndpoint: "https://cognito-idp.us-west-2.amazonaws.com",
				},
			},
			Storage: {
				S3: {
					bucket: "outrun-system-software-us-west-2",
					region: "us-west-2",
				},
			},
		},
	},
};

export const getAwsExports = (environment: Environment): ResourcesConfig => {
	const activeRegion = getActiveRegion(environment);
	return AwsExportsConfig[environment][activeRegion];
};

/**
 * Reactive version of getAwsExports - use this in component/composable code
 * instead of hand-wrapping getAwsExports in your own computed().
 */
export const useAwsExports = (
	environment: MaybeRefOrGetter<Environment>,
): ComputedRef<ResourcesConfig> =>
	computed(() => getAwsExports(toValue(environment)));
